import React from 'react';
import { Info } from 'lucide-react';
import type { Question } from '../../../../types/publicForm.types';

interface QuestionHeaderProps {
  question: Question;
  className?: string;
}

export const QuestionHeader: React.FC<QuestionHeaderProps> = ({ question, className = '' }) => {
  return (
    <label
      htmlFor={question.id}
      className={`block text-sm font-medium text-gray-900 ${className}`}
    >
      {question.label}
      {question.required && (
        <span className="text-red-500 ml-1">*</span>
      )}
    </label>
  );
};

interface QuestionFooterProps {
  error?: string;
  characterLimit?: number;
  characterMinimum?: number;
  currentLength?: number;
  helpText?: string;
  moreInfo?: string;
  isMoreInfoExpanded?: boolean;
  onToggleMoreInfo?: () => void;
  className?: string;
}

export const QuestionFooter: React.FC<QuestionFooterProps> = ({
  error,
  characterLimit = 0,
  characterMinimum = 0,
  currentLength = 0,
  helpText,
  moreInfo,
  isMoreInfoExpanded = false,
  onToggleMoreInfo,
  className = ''
}) => {
  // Determine text color for character count
  const getCharacterCountColor = () => {
    if (characterMinimum > 0 && currentLength < characterMinimum) {
      return 'text-amber-600';
    }
    if (characterLimit > 0 && currentLength > characterLimit * 0.9) {
      return 'text-amber-600';
    }
    return 'text-gray-500';
  };

  // Build character count text
  const getCharacterCountText = () => {
    if (characterLimit > 0 && characterMinimum > 0) {
      return `${currentLength} characters (${characterMinimum} min, ${characterLimit} max)`;
    } else if (characterLimit > 0) {
      return `${currentLength} / ${characterLimit} characters`;
    } else if (characterMinimum > 0) {
      return `${currentLength} / ${characterMinimum} minimum characters`;
    }
    return null;
  };

  const characterCountText = getCharacterCountText();

  return (
    <div className={`${className}`}>
      {/* Help text, error, and character count in flex container */}
      <div className="mt-1 flex items-start justify-between gap-2">
        {/* Left side: Help text and error */}
        <div className="flex-1 space-y-1">
          {/* Help text (always visible) */}
          {helpText && (
            <p className="text-sm text-gray-500 flex items-start gap-1">
              <Info size={14} className="flex-shrink-0 mt-0.5" />
              <span>{helpText}</span>
            </p>
          )}

          {/* Error message (pushes help text up when present) */}
          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
        </div>

        {/* Right side: Character count (fixed position) */}
        {characterCountText && (
          <p className={`text-sm flex-shrink-0 ${getCharacterCountColor()}`}>
            {characterCountText}
          </p>
        )}
      </div>

      {/* More info with reduced spacing */}
      {moreInfo && onToggleMoreInfo && (
        <div className="mt-2 mb-4">
          <button
            type="button"
            onClick={onToggleMoreInfo}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            {isMoreInfoExpanded ? 'Less Information' : 'More Information'}
          </button>
          {isMoreInfoExpanded && (
            <div className="mt-2 p-3 bg-gray-50 text-sm text-gray-600 rounded-lg">
              {moreInfo}
            </div>
          )}
        </div>
      )}
    </div>
  );
};